// Run / step status → colour + display label. Shared by the Home live-activity
// tile, the run-history grids, and the run-detail blade so every surface shows
// the same state colours. Keys are the lowercase status text the API emits
// (run statuses: pending/running/succeeded/failed/timedout/cancelled; step
// statuses add inflight). statusColor() lowercases its input so it tolerates
// either casing.

// Status → brand token (DESIGN.md §2). These are the design system's semantic
// colours, not generic hexes, so every status surface (run grids, the dashboard,
// the Designer test panel, the live-activity tile) tracks the palette and the
// light-theme remap. Active states borrow the one brand accent; terminal states
// use the success/danger/warn tokens; quiescent states stay neutral.
export const STATUS_COLORS: Record<string, string> = {
  pending: 'var(--fg-muted)',
  running: 'var(--accent)',
  // AAI-133: the main path has ended and the On-finish lane is reporting the outcome. Still
  // active, so it takes the active accent — never a terminal colour.
  finishing: 'var(--accent)',
  inflight: 'var(--accent)', // step-level "InFlight" → active, the brand accent
  // Awaiting-human states (agent runs emit NeedsInput / NeedsApproval). These are
  // the most actionable run state, so they take the attention colour (--warn) —
  // deliberately distinct from running/inflight (--accent) so "parked, needs a
  // human" never reads as "still working". The pill's text label carries the
  // meaning too, so the state is never colour-alone.
  needsinput: 'var(--warn)',
  needsapproval: 'var(--warn)',
  succeeded: 'var(--success)',
  failed: 'var(--danger)',
  timedout: 'var(--warn)',
  cancelled: 'var(--fg-muted)',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  running: 'Running',
  finishing: 'Finishing',
  inflight: 'In flight',
  needsinput: 'Needs input',
  needsapproval: 'Needs approval',
  succeeded: 'Succeeded',
  failed: 'Failed',
  timedout: 'Timed out',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

export function statusColor(status: string): string {
  return STATUS_COLORS[status.toLowerCase()] ?? 'var(--fg-muted)';
}

export function statusLabel(status: string): string {
  if (!status) return '—';
  const key = status.toLowerCase();
  return STATUS_LABELS[key] ?? status.charAt(0).toUpperCase() + status.slice(1);
}
